import type { ComparisonOp, ConditionNode, IndicatorDef, Operand } from "@strat/format";

import { indicatorDisplayName } from "./indicatorStyle";

/**
 * A condition tree as one line of English, in the builder's own words.
 *
 * For reading what a bot is running without opening the builder — and without
 * reading JSON, which is how "what did I actually deploy" got answered before.
 */

const OP_WORDS: Record<ComparisonOp, string> = {
  gt: "is above",
  lt: "is below",
  crosses_above: "crosses above",
  crosses_below: "crosses below",
};

/** "1 bar ago", "3 bars ago", or nothing for the current bar. */
export function barsAgoText(offset: number | undefined): string {
  if (!offset) return "";
  return ` ${offset} bar${offset === 1 ? "" : "s"} ago`;
}

export function operandText(operand: Operand, indicators: readonly IndicatorDef[]): string {
  switch (operand.kind) {
    case "const":
      return String(operand.value);
    case "price":
      return `${operand.field}${barsAgoText(operand.offset)}`;
    case "indicator": {
      const def = indicators.find((d) => d.id === operand.ref);
      const name = def ? indicatorDisplayName(def, undefined, indicators) : operand.ref;
      return `${operand.output ? `${name} ${operand.output}` : name}${barsAgoText(operand.offset)}`;
    }
  }
}

export function conditionText(node: ConditionNode, indicators: readonly IndicatorDef[], depth = 0): string {
  if ("children" in node) {
    if (node.children.length === 0) return node.op === "and" ? "always" : "never";
    const parts = node.children.map((child) => conditionText(child, indicators, depth + 1));
    const joined = parts.join(node.op === "and" ? " and " : " or ");
    // Brackets only where a reader could otherwise group it the wrong way.
    return depth > 0 && parts.length > 1 ? `(${joined})` : joined;
  }
  return `${operandText(node.left, indicators)} ${OP_WORDS[node.op]} ${operandText(node.right, indicators)}`;
}
